import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { ShoppingBag, X, Sparkles } from "lucide-react";
import { imgUrl } from "@/config";
import { computeRentalPrice, computeByob, MIN_RENTAL_SUBTOTAL } from "@/utils/pricing";

/**
 * FloatingCart — bottom-right pill on desktop, tap-open bottom drawer on mobile.
 * When the drawer is open the catalog still fills the whole width; the drawer
 * simply overlays from the bottom so shoppers can peek at their booking and
 * jump straight to /checkout.
 */
export const FloatingCart = ({
  cart, setCart, products, range, zones, onIncrement,
  cartOptions = {},
  zoneId, resort, address, addrCheck, isOffProperty,
  deliverySlot, pickupSlot,
  differentPickup, pickupIsOffProperty, pickupZoneId, pickupResort, pickupAddress,
  onEditTrip,
  byob,
  cartAddons = {},
}) => {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const totalQty = Object.values(cart).reduce((a, b) => a + Number(b || 0), 0);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const items = useMemo(() => Object.entries(cart)
    .filter(([, q]) => q > 0)
    .map(([product_id, quantity]) => {
      const p = (products || []).find((x) => x.id === product_id);
      const optIds = cartOptions[product_id] || [];
      // Look up chosen option names so the drawer reads like the cart on the
      // main page (e.g. "Zoe Stroller · Snack tray (+$2/day)").
      const chosenOptions = optIds.flatMap((oid) => {
        for (const g of (p?.option_groups || [])) {
          const o = (g.options || []).find((x) => x.id === oid);
          if (o) return [o];
        }
        return [];
      });
      return { product_id, quantity, product: p, chosenOptions, addonFor: cartAddons[product_id] || null };
    })
    .filter((x) => x.product), [cart, products, cartOptions, cartAddons]);

  // Billable days mirror backend calc_quote: inclusive date span, 3-day minimum.
  const msPerDay = 1000 * 60 * 60 * 24;
  const actualDays = range?.from && range?.to
    ? Math.max(1, Math.round((new Date(range.to) - new Date(range.from)) / msPerDay) + 1)
    : 0;
  const billableDays = actualDays > 0 ? Math.max(3, actualDays) : 0;

  const dateHint = range?.from && range?.to
    ? `${format(range.from, "MMM d")}–${format(range.to, "MMM d")}`
    : "Pick your dates";

  const updateQty = (id, delta) => {
    if (!setCart) return;
    if (delta > 0 && onIncrement) { onIncrement(id); return; }
    setCart((c) => {
      const next = { ...c, [id]: Math.max(0, (c[id] || 0) + delta) };
      if (next[id] === 0) delete next[id];
      return next;
    });
  };

  const activeZone = zones ? zones.find((z) => z.id === zoneId) : null;

  // $50 rental-merchandise minimum — rental items only, pre-tax/fees.
  const rentalSubtotal = items.reduce((sum, { product, quantity, chosenOptions }) => {
    const pr = computeRentalPrice(product, range);
    if (!pr) return sum;
    const delta = (chosenOptions || []).reduce((s, o) => s + Number(o.price_delta_per_day || 0), 0);
    return sum + Math.floor(pr.rate + delta) * pr.billableDays * quantity;
  }, 0);
  // Build-Your-Own Bundle preview — mirrors backend calc_quote (eligible lines × %).
  const byobState = computeByob(byob, products, cart);
  // Parents first, each add-on directly under the item it belongs to.
  const orderedItems = useMemo(() => [...items.filter((i) => !i.addonFor), ...items.filter((i) => i.addonFor)]
    .sort((a, b) => {
      const ka = a.addonFor || a.product_id, kb = b.addonFor || b.product_id;
      if (ka !== kb) return items.findIndex((i) => i.product_id === ka) - items.findIndex((i) => i.product_id === kb);
      return (a.addonFor ? 1 : 0) - (b.addonFor ? 1 : 0);
    }), [items]);

  if (totalQty === 0) return null;
  const byobEligibleSubtotal = items.reduce((sum, { product, quantity, chosenOptions }) => {
    if (!product.byob_eligible) return sum;
    const pr = computeRentalPrice(product, range);
    if (!pr) return sum;
    const delta = (chosenOptions || []).reduce((s, o) => s + Number(o.price_delta_per_day || 0), 0);
    return sum + Math.floor(pr.rate + delta) * pr.billableDays * quantity;
  }, 0);
  const byobDiscount = byobState?.qualified ? Math.round(byobEligibleSubtotal * byobState.percent) / 100 : 0;
  const belowMin = !!(range?.from && range?.to) && items.length > 0 && rentalSubtotal < MIN_RENTAL_SUBTOTAL;
  const minShortfall = Math.ceil(MIN_RENTAL_SUBTOTAL - rentalSubtotal);

  const canProceed = (() => {
    if (!setCart) return false; // legacy call site without full context
    if (!range?.from || !range?.to) return false;
    if (items.length === 0) return false;
    if (belowMin) return false;
    if (!activeZone) return false;
    if (activeZone.requires_address && !addrCheck?.delivers) return false;
    if (!activeZone.requires_address && !resort) return false;
    if (!deliverySlot || !pickupSlot) return false;
    return true;
  })();

  const proceed = () => {
    if (!canProceed) {
      if (!range?.from || !range?.to) { onEditTrip?.(); return toast.error("Please choose your dates & delivery first"); }
      if (items.length === 0) return toast.error("Add at least one item");
      if (belowMin) return toast.error(`$${MIN_RENTAL_SUBTOTAL} rental minimum — add $${minShortfall} more in rental items to complete your reservation.`);
      if (!activeZone) { onEditTrip?.(); return toast.error("Please choose your delivery destination first"); }
      if (activeZone.requires_address && !addrCheck?.delivers) return toast.error("Please verify your delivery address first");
      if (!activeZone.requires_address && !resort) return toast.error("Pick your resort");
      if (!deliverySlot || !pickupSlot) { onEditTrip?.(); return toast.error("Please pick both a delivery & pickup 1-hour window"); }
      return;
    }
    const pickupZone = differentPickup ? zones.find((z) => z.id === pickupZoneId) : null;
    setOpen(false);
    nav("/checkout", {
      state: {
        cart,
        cartOptions,
        cartAddons,
        range: { from: range.from.toISOString(), to: range.to.toISOString() },
        zoneId,
        delivery_resort: activeZone.requires_address ? (address || activeZone.area) : resort,
        deliverySlot,
        pickupSlot,
        address,
        isOffProperty,
        differentPickup,
        pickup_zone_id: differentPickup ? pickupZoneId : null,
        pickup_resort: differentPickup ? (pickupZone?.requires_address ? (pickupAddress || pickupZone.area) : pickupResort) : null,
        pickup_address: differentPickup && pickupZone?.requires_address ? pickupAddress : null,
        pickupIsOffProperty,
      },
    });
  };

  const openDrawer = () => {
    if (!setCart) {
      // Legacy behaviour — no drawer wiring passed in, just scroll.
      document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        data-testid="floating-cart"
        className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40 group flex items-center gap-3 pl-4 pr-5 py-3 rounded-full text-[#0A2438] font-semibold shadow-[0_10px_30px_rgba(0,0,0,0.55)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(0,0,0,0.65)]"
        style={{ background: "linear-gradient(135deg,#F4A93A,#FFD166,#D97B37)" }}
        aria-label={`View your booking — ${totalQty} item${totalQty === 1 ? "" : "s"}`}
      >
        <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-[#0A2438]/15">
          <ShoppingBag className="w-4 h-4 text-[#0A2438]" />
          <span className="absolute -top-1 -right-1 bg-[#0A2438] text-[#FFD166] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border border-[#FFD166]/60" data-testid="floating-cart-count">
            {totalQty}
          </span>
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-sm">Review booking</span>
          <span className="text-[10px] font-normal text-[#0A2438]/75">{dateHint}</span>
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
          onClick={() => setOpen(false)}
          data-testid="cart-drawer-overlay"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md sm:mb-0 mb-0 rounded-t-3xl sm:rounded-3xl bg-[#0F1E2C] border border-[#FFD166]/25 shadow-[0_-20px_60px_rgba(0,0,0,0.6)] max-h-[85vh] flex flex-col animate-drawer-up"
            data-testid="cart-drawer"
          >
            <div className="p-4 pb-3 border-b border-[#FFD166]/10 flex items-center justify-between sticky top-0 bg-[#0F1E2C] rounded-t-3xl z-10">
              <div className="flex items-center gap-2">
                <span className="w-8 h-1 bg-[#FFD166]/30 rounded-full sm:hidden absolute -top-3 left-1/2 -translate-x-1/2" />
                <Sparkles className="w-4 h-4 text-[#FFD166]" />
                <div>
                  <div className="font-serif text-lg text-[#F6F1E4] leading-tight">Your booking</div>
                  <div className="text-[10px] text-[#B9C8D5]">{dateHint}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full border border-[#FFD166]/30 flex items-center justify-center text-[#FFD166] hover:bg-[#FFD166]/10"
                aria-label="Close cart"
                data-testid="cart-drawer-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {orderedItems.map(({ product_id, product, quantity, chosenOptions, addonFor }) => (
                <div key={product_id} className={`flex items-center gap-3 pb-3 border-b border-[#FFD166]/10 ${addonFor ? "ml-5 pl-3 border-l-2 border-l-[#FFD166]/30" : ""}`} data-testid={`drawer-item-${product_id}`}>
                  <img src={imgUrl(product.image)} alt={product.name} className={`${addonFor ? "w-11 h-11" : "w-14 h-14"} rounded-lg object-cover shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-sm text-[#F6F1E4] leading-tight truncate">{product.name}</div>
                    {addonFor && (
                      <div className="text-[10px] text-[#FFD166] leading-tight truncate" data-testid={`drawer-item-${product_id}-addon-for`}>
                        add-on for {products.find((p) => p.id === addonFor)?.name || "your item"}
                      </div>
                    )}
                    {chosenOptions && chosenOptions.length > 0 && (
                      <div className="text-[10px] text-[#FFD166] leading-tight truncate" data-testid={`drawer-item-${product_id}-options`}>
                        with {chosenOptions.map((o) => `${o.name}${Number(o.price_delta_per_day) > 0 ? ` (+$${Number(o.price_delta_per_day).toFixed(2)}/day)` : ""}`).join(" · ")}
                      </div>
                    )}
                    <div className="text-[11px] text-[#B9C8D5]" data-testid={`drawer-item-${product_id}-rate`}>
                      ${computeRentalPrice(product, range)?.rate ?? Math.floor(Number(product.tier_1_3 ?? product.price_per_day) || 0)}/day
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => updateQty(product_id, -1)} className="w-7 h-7 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`drawer-dec-${product_id}`}>−</button>
                    <span className="w-6 text-center text-sm">{quantity}</span>
                    <button onClick={() => updateQty(product_id, +1)} className="w-7 h-7 rounded-full border border-[#FFD166]/30 text-[#FFD166]" data-testid={`drawer-inc-${product_id}`}>+</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-[#FFD166]/15 space-y-2 sticky bottom-0 bg-[#0F1E2C] rounded-b-3xl">
              {rentalSubtotal > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#B9C8D5]">
                    {billableDays > 0
                      ? `Rental subtotal (${billableDays} days${actualDays < 3 ? " · 3-day min" : ""})`
                      : "Rental subtotal"}
                  </span>
                  <span className="text-[#FFD166] font-serif text-lg" data-testid="drawer-subtotal">${rentalSubtotal.toFixed(2)}</span>
                </div>
              )}
              {byobState && byobState.qualified && byobDiscount > 0 && (
                <div className="flex items-center justify-between text-sm" data-testid="drawer-byob-line">
                  <span className="text-emerald-400">Build-Your-Own Bundle ({byobState.percent}% off baby gear)</span>
                  <span className="text-emerald-400 font-serif">−${byobDiscount.toFixed(2)}</span>
                </div>
              )}
              {byobState && !byobState.qualified && byobState.units > 0 && (
                <p className="text-[11px] text-[#F4A93A]" data-testid="drawer-byob-hint">
                  Add {byobState.remaining} more baby {byobState.remaining === 1 ? "item" : "items"} to unlock {byobState.percent}% off your bundle.
                </p>
              )}
              {belowMin && (
                <p className="text-[11px] text-[#F4A93A]" data-testid="drawer-min-order-warning">
                  ${MIN_RENTAL_SUBTOTAL} rental minimum — add ${minShortfall} more in rental items to complete your reservation.
                </p>
              )}
              <button
                type="button"
                onClick={proceed}
                disabled={!canProceed}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="drawer-checkout-btn"
              >
                {canProceed ? "Reserve & Pay Securely" : (belowMin ? `Add $${minShortfall} more to check out` : "Almost there — finish your trip first")}
              </button>
              <p className="text-[10px] text-center text-[#B9C8D5]/70">Delivery fees + tax are calculated at the next step.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
